/**
 * Kicks off loading for every distinct font a face's TEXT / TEXT_LIST elements use, then waits for
 * `document.fonts.ready`.
 *
 * `document.fonts.ready` only resolves once every font the page has *already asked for* has
 * finished loading — it does not itself trigger a load. The cart preview canvas draws before any
 * of these custom faces (Romie, Amalfi Coast, ...) have necessarily been painted anywhere on the
 * page, so nothing may have asked the browser to fetch them yet. Explicitly requesting each font
 * here, before awaiting `ready`, closes that gap.
 *
 * Nothing here may ever block or break add to bag: every failure is swallowed so a font that never
 * loads (or a browser with no `document.fonts`) just falls back silently, as it already does.
 */

import { isUvInsertTextListElement, type UVInsertFaceElement } from "@shared/models/uv-inserts"

/**
 * The weights the card canvas actually draws with.
 *
 * The timeline headline is drawn at 600, the moment line at 300 (Figtree Light) and everything else
 * at 400, and `document.fonts.load` resolves a weight at a time: asking only for the default would
 * leave the other faces unrequested, so `document.fonts.ready` would not wait for them and those
 * lines would paint in a fallback.
 */
const DRAWN_FONT_WEIGHTS = [300, 400, 600] as const

/**
 * Requests every weight of every distinct font family used by `face`'s TEXT and TEXT_LIST
 * elements, then awaits `document.fonts.ready`.
 *
 * @param options - Options object.
 * @param options.face - The face whose `elementConfig` fonts should be loaded.
 */
export async function ensureFaceFontsLoaded({ face }: { face: UVInsertFaceElement }): Promise<void> {
  const fonts = new Set<string>()
  for (const element of face.elementConfig ?? []) {
    if (element.type === "TEXT" || isUvInsertTextListElement(element)) {
      if (element.font) fonts.add(element.font)
    }
  }

  if (document.fonts?.load) {
    // In parallel, and each request swallows its own failure: these run on the add to bag path,
    // and one missing weight must neither delay the others nor take them down with it.
    await Promise.all(
      Array.from(fonts).flatMap((font) =>
        DRAWN_FONT_WEIGHTS.map(async (weight) => {
          try {
            await document.fonts.load(`${weight} 10px "${font}"`)
          } catch {
            // A font that fails to load falls back silently, as it already does without this call.
          }
        })
      )
    )
  }

  try {
    await document.fonts?.ready
  } catch {
    // A font manifest that never settles is not worth losing the preview over.
  }
}
